// @ts-nocheck
// Client-facing bulk profile manager: see all my Facebook accounts, manage phone
// numbers, and send connect links (one or all at once). The connect link opens the
// login flow on demand — no browser is spawned just by sending.
import React, {useEffect, useMemo, useRef, useState} from 'react'
import axios from 'axios'
import {PageTitle} from '../../../_metronic/layout/core'

const API_URL = process.env.REACT_APP_API_URL
const FB_ACCESS_TOKEN = process.env.REACT_APP_FB_ACCESS_TOKEN || ''

// Prefer the live FB Graph picture, fall back to a stored URL.
const picUrl = (p: any) =>
  p?.facebook_id && FB_ACCESS_TOKEN
    ? `https://graph.facebook.com/${p.facebook_id}/picture?type=square&access_token=${FB_ACCESS_TOKEN}`
    : p?.profile_picture || ''

const relTime = (iso: any) => {
  if (!iso) return 'מעולם לא'
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000)
  if (min < 1) return 'הרגע'
  if (min < 60) return `לפני ${min} ד׳`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `לפני ${hr} ש׳`
  return `לפני ${Math.floor(hr / 24)} י׳`
}

const api = () => {
  const inst = axios.create()
  inst.interceptors.request.use((config) => {
    try {
      const raw = localStorage.getItem('kt-auth-react-v')
      const token = raw ? JSON.parse(raw)?.api_token : null
      if (token && config.headers) config.headers.Authorization = `Bearer ${token}`
    } catch (_) {}
    return config
  })
  return inst
}

const BulkProfilesWrapper = () => {
  const [enabled, setEnabled] = useState<boolean | null>(null)
  const [profiles, setProfiles] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [phones, setPhones] = useState<Record<string, string>>({})
  const [sentFor, setSentFor] = useState<Record<string, boolean>>({})
  const [filter, setFilter] = useState('all') // all | connected | disconnected
  const [query, setQuery] = useState('')
  const [wa, setWa] = useState<any>({status: 'disconnected'}) // per-account WhatsApp session
  const [waBusy, setWaBusy] = useState(false)
  const waPoll = useRef<any>(null)
  const [maxProfiles, setMaxProfiles] = useState(0)
  const [used, setUsed] = useState(0)
  const [addCount, setAddCount] = useState(1)
  const [adding, setAdding] = useState(false)
  const [revealed, setRevealed] = useState<Record<string, boolean>>({})

  const copy = (txt: string) => {
    try {
      navigator.clipboard.writeText(txt)
    } catch (_) {}
  }

  const load = async () => {
    setLoading(true)
    try {
      const res = await api().get(`${API_URL}/api/my-profiles`)
      const list = res.data?.profiles || []
      setProfiles(list)
      setMaxProfiles(res.data?.max_self_profiles || 0)
      setUsed(res.data?.used || list.length)
      const ph: Record<string, string> = {}
      list.forEach((p: any) => (ph[p._id] = p.login_link_phone || ''))
      setPhones(ph)
    } catch (e) {
      console.error(e)
    }
    setLoading(false)
  }
  useEffect(() => {
    (async () => {
      try {
        const res = await api().get(`${API_URL}/api/my-profiles/access`)
        const ok = !!res.data?.enabled
        setEnabled(ok)
        if (ok) {
          setMaxProfiles(res.data?.max_self_profiles || 0)
          load()
        } else {
          setLoading(false)
        }
      } catch (_) {
        setEnabled(false)
        setLoading(false)
      }
    })()
  }, [])

  const addProfiles = async () => {
    setAdding(true)
    try {
      await api().post(`${API_URL}/api/my-profiles/add`, {count: addCount})
      await load()
    } catch (e: any) {
      alert(e?.response?.data?.error || 'הוספה נכשלה')
    }
    setAdding(false)
  }

  const sendOne = async (p) => {
    const ph = (phones[p._id] || '').trim()
    if (!ph) return alert('הזן מספר טלפון')
    if (!waConnected) return alert('חבר קודם את הוואטסאפ שלך (סרוק את הקוד למעלה)')
    try {
      await api().post(`${API_URL}/api/my-profiles/${p._id}/send-login-link`, {phone: ph})
      setSentFor((m) => ({...m, [p._id]: true}))
    } catch (e: any) {
      const err = e?.response?.data?.error
      if (err === 'WhatsApp not connected') return alert('הוואטסאפ אינו מחובר. סרוק את הקוד למעלה ונסה שוב.')
      alert('שליחה נכשלה: ' + (err || e?.message))
    }
  }

  // --- Per-account WhatsApp (Baileys) session: scan QR, send links from own number ---
  const refreshWa = async () => {
    try {
      const res = await api().get(`${API_URL}/api/my-profiles/whatsapp/status`)
      setWa(res.data || {status: 'disconnected'})
    } catch (_) {}
  }
  useEffect(() => {
    if (enabled !== true) return
    refreshWa()
    waPoll.current = setInterval(refreshWa, 3000)
    return () => clearInterval(waPoll.current)
  }, [enabled])

  const waConnect = async () => {
    setWaBusy(true)
    try {
      await api().post(`${API_URL}/api/my-profiles/whatsapp/connect`)
      await refreshWa()
    } catch (e: any) {
      alert('שירות הוואטסאפ אינו זמין כרגע: ' + (e?.response?.data?.error || e?.message))
    }
    setWaBusy(false)
  }
  const waLogout = async () => {
    setWaBusy(true)
    try {
      await api().post(`${API_URL}/api/my-profiles/whatsapp/logout`)
      await refreshWa()
    } catch (_) {}
    setWaBusy(false)
  }
  const waConnected = wa?.status === 'connected'
  // The worker rewrites the QR every ~20s while the pairing socket is alive. A QR that
  // hasn't been refreshed for over a minute means the socket died (e.g. worker restart) —
  // treat it as dead and fall back to the Connect button rather than showing a stale code.
  // (re-evaluated each 3s poll tick, which re-renders this component.)
  const waQrFresh = !!wa?.qr && !!wa?.qr_at && Date.now() - new Date(wa.qr_at).getTime() < 60000

  const counts = useMemo(() => {
    const r = {all: profiles.length, connected: 0, disconnected: 0}
    profiles.forEach((p) => (p.connected ? r.connected++ : r.disconnected++))
    return r
  }, [profiles])

  const shown = useMemo(() => {
    return profiles.filter((p) => {
      const mq = !query || `${p.name} ${phones[p._id] || ''}`.toLowerCase().includes(query.toLowerCase())
      const mf = filter === 'all' || (filter === 'connected' ? p.connected : !p.connected)
      return mq && mf
    })
  }, [profiles, query, filter, phones])

  if (enabled === false) {
    return (
      <>
        <PageTitle breadcrumbs={[]}>חיבור פרופילים</PageTitle>
        <div className='card'><div className='card-body text-center text-muted py-10'>
          הפיצ׳ר אינו מופעל בחשבון זה. פנו לתמיכה כדי להפעיל ניהול פרופילים בכמות.
        </div></div>
      </>
    )
  }

  const atCap = maxProfiles > 0 && used >= maxProfiles

  return (
    <>
      <PageTitle breadcrumbs={[]}>חיבור פרופילים</PageTitle>

      <div className='card mb-5'>
        <div className='card-body d-flex flex-wrap gap-4 align-items-center justify-content-between py-4'>
          <div className='d-flex flex-wrap align-items-center gap-3'>
            <div>
              <span className='fw-bold fs-5'>{used}</span>
              <span className='text-muted'> / {maxProfiles} פרופילים בשימוש</span>
              {atCap && <span className='badge badge-light-warning ms-2'>הגעת למכסה — פנה לתמיכה לתוספת</span>}
            </div>
            {/* Logged in vs not: `connected` from the API is is_logged_in && facebook_id,
                i.e. profiles that can actually post right now. */}
            <span className='badge badge-light-success fs-7'>
              <i className='bi bi-check-circle me-1' />{counts.connected} מחוברים לפייסבוק
            </span>
            {counts.disconnected > 0 && (
              <span className='badge badge-light-danger fs-7'>
                <i className='bi bi-exclamation-circle me-1' />{counts.disconnected} לא מחוברים
              </span>
            )}
          </div>
          <div className='d-flex gap-2 align-items-center'>
            <input type='number' min={1} className='form-control form-control-sm' style={{width: 80}} value={addCount} onChange={(e) => setAddCount(parseInt(e.target.value) || 1)} disabled={atCap} />
            <button className='btn btn-sm btn-primary' disabled={adding || atCap} onClick={addProfiles}>
              <i className='bi bi-plus-lg me-1' /> {adding ? 'מוסיף…' : 'הוסף פרופילים'}
            </button>
          </div>
        </div>
      </div>

      <div className='card mb-5'>
        <div className='card-body py-5'>
          <div className='d-flex flex-wrap align-items-center justify-content-between gap-4'>
            <div>
              <div className='fw-bold fs-5'><i className='bi bi-whatsapp text-success me-2' />וואטסאפ לשליחת קישורים</div>
              <div className='text-muted fs-7'>חבר את הוואטסאפ שלך — קישורי ההתחברות יישלחו מהמספר שלך.</div>
            </div>
            <div className='d-flex align-items-center gap-3'>
              {waConnected ? (
                <>
                  <span className='badge badge-light-success fs-7'>
                    <i className='bi bi-check-circle me-1' /> מחובר
                    {wa?.number ? <span dir='ltr' className='ms-1'>· {wa.number}</span> : null}
                  </span>
                  <button className='btn btn-sm btn-light-danger' disabled={waBusy} onClick={waLogout}>
                    <i className='bi bi-box-arrow-right me-1' /> ניתוק
                  </button>
                </>
              ) : wa?.status === 'connecting' && waQrFresh ? (
                <div className='text-center'>
                  <div className='p-2 bg-white rounded border d-inline-block'>
                    <img src={wa.qr} alt='WhatsApp QR' style={{width: 180, height: 180}} />
                  </div>
                  <div className='text-muted fs-8 mt-2'><span className='spinner-border spinner-border-sm me-1' /> סרוק מהוואטסאפ בטלפון…</div>
                  <div className='mt-1'>
                    <button className='btn btn-link btn-sm p-0 fs-8' disabled={waBusy} onClick={waConnect}>
                      <i className='bi bi-arrow-clockwise me-1' />הקוד לא עובד? צור קוד חדש
                    </button>
                  </div>
                </div>
              ) : wa?.status === 'connecting' && !wa?.qr ? (
                <span className='text-muted fs-7'><span className='spinner-border spinner-border-sm me-2' />יוצר קוד QR…</span>
              ) : (
                <button className='btn btn-sm btn-success' disabled={waBusy} onClick={waConnect}>
                  <i className='bi bi-whatsapp me-1' /> {waBusy ? 'מתחבר…' : 'חבר וואטסאפ (סרוק קוד)'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className='card'>
        <div className='card-header border-0 pt-6 d-flex flex-wrap gap-3 align-items-center justify-content-between'>
          <div className='d-flex gap-2 flex-wrap'>
            {[
              {id: 'all', label: `הכל (${counts.all})`},
              {id: 'disconnected', label: `לא מחוברים (${counts.disconnected})`},
              {id: 'connected', label: `מחוברים (${counts.connected})`},
            ].map((f) => (
              <button key={f.id} className={`btn btn-sm ${filter === f.id ? 'btn-primary' : 'btn-light'}`} onClick={() => setFilter(f.id)}>
                {f.label}
              </button>
            ))}
            <input className='form-control form-control-sm' style={{width: 220}} placeholder='חיפוש…' value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
        </div>

        <div className='card-body py-4'>
          {loading ? (
            <div className='text-center py-10'><span className='spinner-border text-primary' /></div>
          ) : shown.length === 0 ? (
            <div className='text-center text-muted py-10'>לא נמצאו פרופילים.</div>
          ) : (
            <div className='table-responsive'>
              <table className='table align-middle table-row-dashed gy-4'>
                <thead>
                  <tr className='text-muted fw-bold fs-7 text-uppercase'>
                    <th>פרופיל</th>
                    <th>פרטי התחברות</th>
                    <th>סטטוס</th>
                    <th>פרסום</th>
                    <th>טלפון וואטסאפ</th>
                    <th className='text-end'>קישור התחברות</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((p) => (
                    <tr key={p._id}>
                      <td className='fw-semibold'>
                        <div className='d-flex align-items-center gap-2'>
                          <div style={{width: 34, height: 34, borderRadius: '50%', background: '#eee', flex: '0 0 34px'}}>
                            {picUrl(p) ? (
                              <img
                                src={picUrl(p)}
                                alt=''
                                referrerPolicy='no-referrer'
                                style={{width: 34, height: 34, borderRadius: '50%', objectFit: 'cover'}}
                                onError={(e) => {(e.currentTarget as any).style.visibility = 'hidden'}}
                              />
                            ) : null}
                          </div>
                          <span>{p.name}</span>
                        </div>
                      </td>
                      <td style={{fontSize: 12}} dir='ltr'>
                        {p.facebook_username ? (
                          <div className='text-muted'>
                            <i className='bi bi-person-badge me-1' />{p.facebook_username}
                            <i className='bi bi-clipboard ms-1' style={{cursor: 'pointer'}} onClick={() => copy(p.facebook_username)} />
                          </div>
                        ) : null}
                        {p.facebook_password ? (
                          <div className='text-muted'>
                            <i className='bi bi-key me-1' />
                            {revealed[p._id] ? p.facebook_password : '••••••'}
                            <i className={`bi ${revealed[p._id] ? 'bi-eye-slash' : 'bi-eye'} ms-1`} style={{cursor: 'pointer'}} onClick={() => setRevealed((m) => ({...m, [p._id]: !m[p._id]}))} />
                            <i className='bi bi-clipboard ms-1' style={{cursor: 'pointer'}} onClick={() => copy(p.facebook_password)} />
                          </div>
                        ) : null}
                        {!p.facebook_username && !p.facebook_password ? <span className='text-muted'>—</span> : null}
                      </td>
                      <td>
                        {p.connected ? (
                          <span className='badge badge-light-success'>מחובר{p.active ? ' · פעיל' : ''}</span>
                        ) : (
                          <span className='badge badge-light-danger'>לא מחובר</span>
                        )}
                        <div className='text-muted fs-8 mt-1'>בדיקה אחרונה: {relTime(p.last_health_check)}</div>
                      </td>
                      <td>
                        {p.posting ? <span className='badge badge-light-success'>פעיל</span> : <span className='badge badge-light-secondary'>—</span>}
                      </td>
                      <td>
                        <input
                          className='form-control form-control-sm'
                          dir='ltr'
                          style={{maxWidth: 170}}
                          placeholder='05X-XXXXXXX'
                          value={phones[p._id] || ''}
                          onChange={(e) => setPhones((m) => ({...m, [p._id]: e.target.value}))}
                        />
                      </td>
                      <td className='text-end' style={{whiteSpace: 'nowrap'}}>
                        {p.connected ? (
                          <span className='text-muted'>—</span>
                        ) : (
                          <>
                            <button className='btn btn-sm btn-light-success me-2' disabled={!(phones[p._id] || '').trim() || !waConnected} title={!waConnected ? 'חבר וואטסאפ קודם (סרוק את הקוד למעלה)' : ''} onClick={() => sendOne(p)}>
                              <i className='bi bi-whatsapp me-1' /> שלח קישור
                            </button>
                            {sentFor[p._id] && <span className='badge badge-light-success me-2'>נשלח ✓</span>}
                            <a className='btn btn-sm btn-light' href={p.connect_url} target='_blank' rel='noreferrer' title='פתח את דף החיבור'>
                              <i className='bi bi-box-arrow-up-left' />
                            </a>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default BulkProfilesWrapper
